<template>
  <div class="page-container">
    <div class="track-toolbar">
      <el-select v-model="deviceId" placeholder="选择设备" style="width:200px" @change="loadTrack" :loading="loading">
        <el-option v-for="d in devices" :key="d.id" :label="d.name + ' / ' + d.sn" :value="d.id" />
      </el-select>
      <el-date-picker v-model="date" type="date" value-format="yyyy-MM-dd" placeholder="日期" style="width:170px" @change="loadTrack" />
      <el-button type="primary" icon="el-icon-video-play" :disabled="!points.length" @click="play">{{ playing ? '暂停' : '回放' }}</el-button>
      <el-button icon="el-icon-refresh-left" :disabled="!points.length" @click="reset">重置</el-button>
      <span class="track-meta" v-if="points.length">
        共 {{ points.length }} 个点 · 超速 {{ overSpeedCount }} 段 · 当前 {{ curIdx + 1 }}/{{ points.length }}
      </span>
    </div>

    <div class="track-body">
      <div class="track-map">
        <TMapPicker mode="track" :center="center" :track-points="points" :track-current="curIdx" :track-pos="currentPos" height="100%" />
      </div>
      <div class="track-side">
        <div class="side-title">车速曲线（红点为超速）</div>
        <div ref="speedChart" class="side-chart"></div>
        <div class="side-title">回放进度</div>
        <el-slider v-model="curIdx" :max="points.length - 1" :disabled="!points.length" @input="onSeek" />
        <div class="cur-info" v-if="points.length">
          <div>时间：{{ points[curIdx].ts }}</div>
          <div>车速：<b :style="{ color: points[curIdx].over ? '#f53f3f' : '#1d2129' }">{{ points[curIdx].speed }} km/h</b></div>
          <div>坐标：{{ points[curIdx].lat }}, {{ points[curIdx].lng }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TMapPicker from '@/views/components/TMapPicker.vue'
import { getTrackDevices, getTrack } from '@/api/sentinel'

export default {
  name: 'TrackIndex',
  components: { TMapPicker },
  data() {
    return {
      devices: [],
      deviceId: '',
      date: '2026-08-18',
      loading: false,
      points: [],
      center: { lat: 30.2741, lng: 120.1551 },
      curIdx: 0,
      progress: 0, // 浮点播放进度（0..points.length-1），用于平滑插值
      playing: false,
      timer: null,
      chart: null
    }
  },
  computed: {
    overSpeedCount() { return this.points.filter(p => p.over).length },
    // 当前位置插值（平滑动画用）：在相邻两点间线性插值
    currentPos() {
      if (!this.points.length) return null
      const i = Math.floor(this.progress)
      const j = Math.min(i + 1, this.points.length - 1)
      const t = this.progress - i
      const a = this.points[i]
      const b = this.points[j]
      return { lat: +(a.lat + (b.lat - a.lat) * t).toFixed(6), lng: +(a.lng + (b.lng - a.lng) * t).toFixed(6) }
    }
  },
  mounted() {
    getTrackDevices().then(res => {
      if (res.code === 200) {
        this.devices = (res.data && res.data.list) || []
        if (this.devices.length) { this.deviceId = this.devices[0].id; this.loadTrack() }
      }
    })
    window.addEventListener('resize', this.resize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
    clearInterval(this.timer)
    if (this.chart) this.chart.dispose()
  },
  methods: {
    loadTrack() {
      if (!this.deviceId) return
      this.loading = true
      this.reset()
      getTrack(this.deviceId, this.date).then(res => {
        if (res.code === 200 && res.data) {
          this.points = res.data.points || []
          this.center = res.data.center || this.center
        }
      }).finally(() => {
        this.loading = false
        this.$nextTick(this.renderChart)
      })
    },
    renderChart() {
      if (!this.$refs.speedChart) return
      if (!this.chart) this.chart = this.$echarts.init(this.$refs.speedChart)
      const cats = this.points.map((p, i) => p.ts.slice(11))
      const data = this.points.map(p => ({ value: p.speed, itemStyle: { color: p.over ? '#f53f3f' : '#165dff' } }))
      this.chart.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 40, right: 16, top: 20, bottom: 50 },
        xAxis: { type: 'category', data: cats, axisLabel: { rotate: 45, fontSize: 10, color: '#86909c' }, axisLine: { lineStyle: { color: '#c9cdd4' } } },
        yAxis: { type: 'value', name: 'km/h', axisLabel: { color: '#86909c' } },
        series: [{ type: 'line', data, showSymbol: false, markLine: { silent: true, symbol: 'none', data: [{ yAxis: 25, label: { formatter: '限速25', color: '#f53f3f' }, lineStyle: { color: '#f53f3f', type: 'dashed' } }] } }]
      })
      this.chart.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: this.curIdx })
    },
    play() {
      if (this.playing) { this.pause(); return }
      if (this.progress >= this.points.length - 1) this.progress = 0
      this.playing = true
      this.timer = setInterval(() => {
        // 每 160ms 前进 0.35 个点：44 点约 20s 播完，点间插值平滑
        this.progress += 0.35
        if (this.progress >= this.points.length - 1) {
          this.progress = this.points.length - 1
          this.applyProgress()
          this.pause()
          return
        }
        this.applyProgress()
      }, 160)
    },
    pause() { this.playing = false; clearInterval(this.timer) },
    reset() { this.pause(); this.progress = 0; this.applyProgress() },
    onSeek(val) {
      this.progress = val
      this.applyProgress()
    },
    // 进度应用到 curIdx（整数，驱动已走蓝线/信息栏）+ 插值位置（驱动 marker 平滑移动）
    applyProgress() {
      const next = Math.floor(this.progress)
      if (next !== this.curIdx) {
        this.curIdx = next
        this.highlight()
      }
    },
    highlight() {
      if (this.chart) this.chart.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: this.curIdx })
    },
    resize() { if (this.chart) this.chart.resize() }
  }
}
</script>

<style scoped>
.track-toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
.track-meta { font-size: 13px; color: #86909c; }
.track-body { display: flex; gap: 16px; height: calc(100vh - 280px); min-height: 420px; }
.track-map { flex: 1; background: #fff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,.1); overflow: hidden; }
.track-map .tmap-picker { height: 100%; }
.track-side { width: 380px; display: flex; flex-direction: column; gap: 8px; }
.side-title { font-size: 14px; font-weight: 600; color: #1d2129; }
.side-chart { flex: 1; min-height: 200px; background: #fff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,.1); padding: 8px; }
.cur-info { background: #fff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,.1); padding: 12px 16px; font-size: 13px; color: #4e5969; line-height: 1.9; }
@media (max-width: 900px) {
  .track-body { flex-direction: column; height: auto; }
  .track-side { width: 100%; }
  .track-map { height: 360px; }
}
</style>
